import React, { useState, useEffect } from "react";
import DropDown from "../DropDown";
import { FaFrown, FaMeh, FaSearch, FaSmile, FaEdit, FaTrash } from "react-icons/fa";
import { useUsers } from "../../context/UsersContext";
import Pagination from "../Pagination";
import NotasEditModal from "./NotasEditModal";

const NotasAdminView = ({ notas, deleteNotaData, updateNotaData, showNotification }) => {
  const { salones, fetchSalones, asignaturas, fetchAsignaturas } = useUsers();
  const [searchValue, setSearchValue] = useState(""); // Filtro por nombre
  const [selectedSalon, setSelectedSalon] = useState(""); // Filtro por salón
  const [selectedAsignatura, setSelectedAsignatura] = useState(""); // Filtro por asignatura
  const [filteredNotas, setFilteredNotas] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingNota, setEditingNota] = useState(null);
  const notasPorPagina = 20;

  const normalizarTexto = (texto) => {
    return texto
      ?.normalize("NFD") // separa las letras de sus acentos
      .replace(/[\u0300-\u036f]/g, "") // elimina los acentos
      .toLowerCase()
      .trim();
  };

  useEffect(() => {
    fetchSalones();
    fetchAsignaturas();
  }, [fetchSalones, fetchAsignaturas]);

  useEffect(() => {
    const filtered = notas.filter((nota) => {
      const matchesSalon =
        selectedSalon === "" || nota.salon_nombre === selectedSalon;
      
      const matchesAsignatura =
        selectedAsignatura === "" || nota.asignatura_nombre === selectedAsignatura;
      const nombreCompleto = [nota.first_name, nota.second_name, nota.first_surname, nota.second_surname]
        .filter(Boolean)
        .join(" ");
      const matchesName =
        searchValue === "" ||
        normalizarTexto(nombreCompleto).includes(normalizarTexto(searchValue));

      return matchesSalon && matchesAsignatura && matchesName;
    });
    setFilteredNotas(filtered);
    setCurrentPage(1); // Reiniciar a la primera página al filtrar
  }, [searchValue, selectedSalon, selectedAsignatura, notas, salones]);

  // Agrupar las notas por estudiante y asignatura
  const groupedNotas = filteredNotas.reduce((acc, nota) => {
    const key = `${nota.students_id}-${nota.asignatura_id}`;
    if (!acc[key]) {
      acc[key] = {
        estudiante: [nota.first_name, nota.second_name, nota.first_surname, nota.second_surname]
          .filter(Boolean)
          .join(" "),
        salon_nombre: nota.salon_nombre,
        asignatura_nombre: nota.asignatura_nombre,
        promedio_final: nota.promedio_final,
        periodos: Array(4).fill(null), // Inicializar 4 periodos vacíos
      };
    }
    acc[key].periodos[nota.periodo_id - 1] = nota; // Asignar la calificación al periodo correspondiente
    return acc;
  }, {});

  const paginatedNotas = Object.values(groupedNotas).slice(
    (currentPage - 1) * notasPorPagina,
    currentPage * notasPorPagina
  );

  const totalPaginas = Math.ceil(
    Object.keys(groupedNotas).length / notasPorPagina
  );

  const renderCalificacion = (nota) => {
    if (!nota) {
      return "-"; // Si la nota es null, mostramos un guion
    }

    if (nota.simbolo_carita) {
      switch (nota.simbolo_carita) {
        case "feliz":
          return (
            <div className="flex justify-center">
              <FaSmile className="text-green text-2xl" />
            </div>
          );
        case "contento":
          return (
            <div className="flex justify-center">
              <FaSmile className="text-main-blue text-2xl" />
            </div>
          );
        case "intermedio":
          return (
            <div className="flex justify-center">
              <FaMeh className="text-yellow-500 text-2xl" />
            </div>
          );
        case "triste":
          return (
            <div className="flex justify-center">
              <FaFrown className="text-red-500 text-2xl" />
            </div>
          );
        default:
          return (
            <div className="flex justify-center">
              {nota.calificacion || "-"}
            </div>
          );
      }
    }

    return (
      <div className="flex justify-center">{nota.calificacion || "-"}</div>
    );
  };

  const handleDelete = async (notaId, estudiante, asignatura) => {
    if (window.confirm(`¿Está seguro de eliminar la nota de ${estudiante} en ${asignatura}?`)) {
      try {
        await deleteNotaData(notaId);
        showNotification({ type: "success", message: "Nota eliminada correctamente." });
      } catch (error) {
        showNotification({ type: "error", message: "Error al eliminar la nota." });
      }
    }
  };

  const handleEdit = (nota) => {
    setEditingNota(nota);
  };

  const handleSaveEdit = async (notaId, formData) => {
    await updateNotaData(notaId, formData);
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-md">
        <h3 className="text-xl font-semibold mb-4">Notas de Estudiantes</h3>

        {/* Filtros */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          {/* Filtro por nombre */}
          <div className="flex items-center w-full md:w-1/4 bg-gray-100 border border-gray-300 rounded-full px-4 py-2">
            <FaSearch className="text-gray-400 mr-2" />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              className="w-full outline-none bg-gray-100 text-gray-600"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>

          {/* Filtro por salón */}
          <div className="w-full md:w-1/4">
            <DropDown
              value={selectedSalon}
              onChange={(e) => setSelectedSalon(e.target.value)}
              options={[{ value: "", label: "Todos los salones" }, ...salones.map((salon) => ({ value: salon.nombre, label: salon.nombre }))]}
              placeholder="Todos los salones"
              
            />
          </div>

          

          {/* Filtro por asignatura */}
          <div className="w-full md:w-1/4">
            <DropDown
              value={selectedAsignatura}
              onChange={(e) => setSelectedAsignatura(e.target.value)}
              options={[{ value: "", label: "Todas las asignaturas" }, ...(asignaturas || []).map((asignatura) => ({ value: asignatura.nombre, label: asignatura.nombre }))]}
              placeholder="Todas las asignaturas"
              
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="overflow-x-auto">
          {Object.keys(groupedNotas).length === 0 ? (
            <p>No hay notas registradas para los filtros seleccionados.</p>
          ) : (
            <table className="text-sm md:text-base table-auto w-full border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray px-4 py-2">Estudiante</th>
                  <th className="border border-gray px-4 py-2">Asignatura</th>
                  <th className="border border-gray px-4 py-2">Periodo 1</th>
                  <th className="border border-gray px-4 py-2">Periodo 2</th>
                  <th className="border border-gray px-4 py-2">Periodo 3</th>
                  <th className="border border-gray px-4 py-2">Periodo 4</th>
                  <th className="border border-gray px-4 py-2">Nota Final</th>
                </tr>
              </thead>
              <tbody>
                {paginatedNotas.map((nota) => (
                  <tr key={`${nota.estudiante}-${nota.asignatura_nombre}`}>
                    <td className="border border-gray px-4 py-2">
                      {nota.estudiante}
                    </td>
                    <td className="border border-gray px-4 py-2">
                      {nota.asignatura_nombre}
                    </td>
                    {nota.periodos.map((periodo, index) => (
                      <td
                        key={index}
                        className="border border-gray px-4 py-2 text-center"
                      >
                        {periodo ? (
                          <div className="flex flex-col gap-2 items-center">
                            {renderCalificacion(periodo)}
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(periodo)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Editar"
                              >
                                <FaEdit size={14} />
                              </button>
                              <button
                                onClick={() => handleDelete(periodo.id, nota.estudiante, nota.asignatura_nombre)}
                                className="text-red-600 hover:text-red-800"
                                title="Eliminar"
                              >
                                <FaTrash size={14} />
                              </button>
                            </div>
                          </div>
                        ) : ("-")}
                      </td>
                    ))}
                    <td className="border border-gray px-4 py-2 text-center font-bold">
                      {nota.promedio_final || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {/* Paginación fuera del contenedor blanco */}
      {Object.keys(groupedNotas).length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPaginas}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Modal de edición */}
      {editingNota && (
        <NotasEditModal
          nota={editingNota}
          onClose={() => setEditingNota(null)}
          onSave={handleSaveEdit}
          showNotification={showNotification}
        />
      )}
    </div>
  );
};

export default NotasAdminView;
